// Spoken cues and beeps. The phone's screen faces away from the rider during
// practice, so audio carries the whole interaction.

export type Tone = 'ready' | 'go' | 'tick' | 'hold' | 'clean' | 'adjust' | 'unassessed' | 'alert';

interface AudioSessionLike {
  type: string;
}

class Speaker {
  private ctx: AudioContext | null = null;
  private voice: SpeechSynthesisVoice | null = null;
  private queue: Promise<void> = Promise.resolve();
  private keep: SpeechSynthesisUtterance[] = [];
  private generation = 0;
  private cancelledAt = 0;
  voiceOn = true;
  beepsOn = true;
  rate = 1.05;
  voiceURI: string | null = null;

  /** Must run inside a user gesture (iOS requires it for both speech and Web Audio). */
  unlock(): void {
    try {
      const session = (navigator as Navigator & { audioSession?: AudioSessionLike }).audioSession;
      if (session) session.type = 'playback'; // play even with the ring/silent switch on
    } catch {
      /* not supported */
    }
    if (!this.ctx) {
      const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (Ctor) this.ctx = new Ctor();
    }
    void this.ctx?.resume();
    if ('speechSynthesis' in window) {
      const u = new SpeechSynthesisUtterance(' ');
      u.volume = 0;
      speechSynthesis.speak(u);
    }
  }

  voices(): SpeechSynthesisVoice[] {
    if (!('speechSynthesis' in window)) return [];
    return speechSynthesis.getVoices().filter((v) => v.lang.toLowerCase().startsWith('en'));
  }

  private pickVoice(): SpeechSynthesisVoice | null {
    const all = this.voices();
    if (this.voiceURI) {
      const v = all.find((x) => x.voiceURI === this.voiceURI);
      if (v) return v;
    }
    if (this.voice && all.includes(this.voice)) return this.voice;
    const lang = (navigator.language || 'en-AU').toLowerCase();
    this.voice =
      all.find((v) => v.lang.toLowerCase() === lang && v.localService) ??
      all.find((v) => v.lang.toLowerCase() === lang) ??
      all.find((v) => /en-(au|gb|nz)/i.test(v.lang)) ??
      all[0] ??
      null;
    return this.voice;
  }

  /** Speaks after anything already queued; `interrupt` cuts the queue short. */
  say(text: string, opts: { interrupt?: boolean } = {}): Promise<void> {
    if (!this.voiceOn || !('speechSynthesis' in window) || !text) return Promise.resolve();
    if (opts.interrupt) this.stopSpeaking();
    const gen = this.generation;
    const run = () =>
      new Promise<void>((resolve) => {
        // Lines queued before a cancel are dropped, not spoken late.
        if (gen !== this.generation) return resolve();
        const u = new SpeechSynthesisUtterance(text);
        const v = this.pickVoice();
        if (v) u.voice = v;
        u.rate = this.rate;
        // Safari can drop end events; never block the practice loop on speech.
        const fallback = setTimeout(resolve, 900 + text.length * 85);
        u.onend = u.onerror = () => {
          clearTimeout(fallback);
          this.keep = this.keep.filter((k) => k !== u);
          resolve();
        };
        this.keep.push(u); // iOS garbage-collects in-flight utterances otherwise
        // Some browsers drop a speak() issued right after cancel().
        const wait = performance.now() - this.cancelledAt < 120 ? 120 : 0;
        if (wait) setTimeout(() => gen === this.generation && speechSynthesis.speak(u), wait);
        else speechSynthesis.speak(u);
      });
    this.queue = this.queue.then(run, run);
    return this.queue;
  }

  stopSpeaking(): void {
    this.generation++;
    this.cancelledAt = performance.now();
    if ('speechSynthesis' in window) speechSynthesis.cancel();
    this.queue = Promise.resolve();
  }

  tone(kind: Tone): void {
    if (!this.beepsOn || !this.ctx) return;
    const ctx = this.ctx;
    const now = ctx.currentTime + 0.01;
    const note = (freq: number, at: number, dur: number, gain = 0.22, type: OscillatorType = 'sine') => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = type;
      o.frequency.value = freq;
      g.gain.setValueAtTime(0, now + at);
      g.gain.linearRampToValueAtTime(gain, now + at + 0.012);
      g.gain.exponentialRampToValueAtTime(0.0008, now + at + dur);
      o.connect(g).connect(ctx.destination);
      o.start(now + at);
      o.stop(now + at + dur + 0.05);
    };
    switch (kind) {
      case 'tick':
        note(660, 0, 0.09, 0.14);
        break;
      case 'ready':
        note(523, 0, 0.12, 0.16);
        note(523, 0.18, 0.12, 0.16);
        break;
      case 'go':
        note(988, 0, 0.32, 0.3, 'triangle');
        break;
      case 'hold':
        note(784, 0, 0.12, 0.18);
        break;
      case 'clean':
        note(659, 0, 0.16, 0.2);
        note(831, 0.1, 0.16, 0.2);
        note(988, 0.2, 0.32, 0.22);
        break;
      case 'adjust':
        note(587, 0, 0.18, 0.2);
        note(523, 0.16, 0.26, 0.2);
        break;
      case 'unassessed':
        note(392, 0, 0.3, 0.18);
        break;
      case 'alert':
        note(440, 0, 0.16, 0.22, 'square');
        note(440, 0.24, 0.16, 0.22, 'square');
        break;
    }
  }
}

export const speaker = new Speaker();
